'use client';

import { c as _c } from "react/compiler-runtime";
import type { Address } from '@morpho-org/blue-sdk';
import { SCREEN_ADDRESS_ROUTE, SCREENING_BLOCKED_MESSAGE, SCREENING_UNAVAILABLE_MESSAGE } from '@repo/address-screening/client';
import { MultiStepDialogContext } from '@repo/ui';
import { Alert } from '@repo/ui/icons';
import { ActionFlowContext, ensureAuthorizedUser, useActionFlow } from '@repo/web3';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { type Dispatch, type ReactNode, type SetStateAction, useContext, useLayoutEffect, useRef, useState } from 'react';
import AcceptToContinueWithoutSimulationConfirmBox from '@/components/common/AcceptToContinueWithoutSimulationConfirmBox/AcceptToContinueWithoutSimulationConfirmBox';
import { useHoldActionFlow, useVaultsActionFlowTracking } from '@/components/providers/VaultsActionFlowProvider';
import { clientEnvs } from '@/config';
import { withDeploymentParam } from '@/config/deployment';
import { useWeb3Context } from '@/contexts/Web3Context';
import { safeSentryBreadcrumb } from '@/helpers/sentry';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import { FeatureFlagEnvironment } from '@/lib/shared/utils/flags.constants';
import { isSimulationUnsupportedSender, SimulationError } from '@/services/simulate/simulate';
const SCREEN_ADDRESS_ENDPOINT = withDeploymentParam(SCREEN_ADDRESS_ROUTE);
interface GateError {
  message: string;
  isBypassable: boolean;
  isRetryBlocked: boolean;
}
interface ScreeningFailure {
  message: string;
  reason: 'blocked' | 'unavailable';
}
export type ActionFlowSimulationBypass = {
  skipFinalSimulation: boolean;
  userBypassed: boolean;
};
const SCREENING_UNAVAILABLE: ScreeningFailure = {
  message: SCREENING_UNAVAILABLE_MESSAGE,
  reason: 'unavailable'
};
const SCREENING_BLOCKED: ScreeningFailure = {
  message: SCREENING_BLOCKED_MESSAGE,
  reason: 'blocked'
};
interface SimulationGateProps {
  finalSimulationError?: unknown;
  /** Reports whether the attempt skipped final simulation and whether the user accepted bypass. */
  onSimulationBypassChange?: (bypass: ActionFlowSimulationBypass) => void;
  children: ReactNode;
}
function toGateError(cause: unknown): GateError {
  if (!(cause instanceof SimulationError)) {
    return {
      message: "Transaction couldn't be simulated. Please try again.",
      isBypassable: false,
      isRetryBlocked: false
    };
  }
  const isDeadSignature = cause.revertCategory === 'signature_expired' || cause.revertCategory === 'stale_nonce' || cause.revertCategory === 'invalid_signature';
  return {
    message: isDeadSignature ? 'The signature is no longer valid. Close this window and start the deposit again to sign a new one.' : cause.message,
    isBypassable: cause.isAcceptable && !isDeadSignature,
    isRetryBlocked: isDeadSignature || cause.details?.code === 'CONSUMER_LIMIT_VIOLATION'
  };
}
function getFinalGateError(errorMessage: string | undefined, cause: unknown): GateError | null {
  if (!errorMessage || cause === undefined) return null;
  return toGateError(cause);
}
interface CtaLabelOptions {
  isPending: boolean;
  skipSimulation: boolean;
  currentError: GateError | null;
  flowLabel: string | undefined;
}
function getCtaLabel({
  isPending,
  skipSimulation,
  currentError,
  flowLabel
}: CtaLabelOptions) {
  if (isPending) return 'Verifying your wallet…';
  if (skipSimulation) return 'Proceed anyway';
  if (currentError) return 'Try again';
  return flowLabel;
}
async function getScreeningFailure(address: Address | undefined, isE2E: boolean, queryClient: QueryClient): Promise<ScreeningFailure | undefined> {
  if (isE2E) return undefined;
  if (!address) return SCREENING_UNAVAILABLE;
  try {
    const verdict = await ensureAuthorizedUser(queryClient, address, {
      screenAddressEndpoint: SCREEN_ADDRESS_ENDPOINT,
      staleTime: 0
    });
    if (verdict.authorized) return undefined;
    if (verdict.reason === 'unavailable') return SCREENING_UNAVAILABLE;
    return SCREENING_BLOCKED;
  } catch {
    return SCREENING_UNAVAILABLE;
  }
}
function reportScreeningFailure({
  failure,
  chainId,
  setScreeningError,
  trackScreeningCancel
}: {
  failure: ScreeningFailure;
  chainId: number | undefined;
  setScreeningError: Dispatch<SetStateAction<GateError | null>>;
  trackScreeningCancel: (chainId: number) => void;
}) {
  const error = {
    message: failure.message,
    isBypassable: false,
    isRetryBlocked: failure.reason === 'blocked'
  };
  setScreeningError(error);
  if (chainId !== undefined) {
    safeSentryBreadcrumb({
      category: 'transaction',
      message: 'Transaction blocked by address screening',
      level: 'warning',
      data: {
        chainId,
        reason: failure.reason
      }
    });
    trackScreeningCancel(chainId);
  }
}
function retainBlockedScreeningError(screeningError: GateError | null) {
  return screeningError?.isRetryBlocked ? screeningError : null;
}
function useGateErrorState(isDialogOpen) {
  const $ = _c(2);
  const [screeningError, setScreeningError] = useState(null);
  const [prevIsDialogOpen, setPrevIsDialogOpen] = useState(isDialogOpen);
  if (prevIsDialogOpen !== isDialogOpen) {
    setPrevIsDialogOpen(isDialogOpen);
    if (!isDialogOpen) {
      setScreeningError(null);
    }
  }
  let t0;
  if ($[0] !== screeningError) {
    t0 = {
      screeningError,
      setScreeningError
    };
    $[0] = screeningError;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export function SimulationGate(t0) {
  const $ = _c(40);
  const {
    finalSimulationError,
    onSimulationBypassChange,
    children
  } = t0;
  const flow = useActionFlow();
  const {
    trackScreeningCancel
  } = useVaultsActionFlowTracking();
  const isDialogOpen = useContext(MultiStepDialogContext)?.isOpen ?? true;
  const {
    allowSimulationBypass,
    evmPreflightTransactionSimulation
  } = useFeatureFlags();
  const {
    account
  } = useWeb3Context();
  const queryClient = useQueryClient();
  const isE2E = clientEnvs.NEXT_PUBLIC_FALLBACK_FLAGS_ENVIRONMENT === FeatureFlagEnvironment.E2E;
  const {
    screeningError,
    setScreeningError
  } = useGateErrorState(isDialogOpen);
  const [pending, setPending] = useState(null);
  const [isBypassAccepted, setIsBypassAccepted] = useState(false);
  let t1;
  if ($[0] !== flow.chainId || $[1] !== flow.execute || $[2] !== isDialogOpen) {
    t1 = {
      execute: flow.execute,
      isDialogOpen,
      chainId: flow.chainId
    };
    $[0] = flow.chainId;
    $[1] = flow.execute;
    $[2] = isDialogOpen;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const latest = t1;
  const latestRef = useRef(latest);
  const attemptRef = useRef(0);
  let t2;
  if ($[4] !== account || $[5] !== evmPreflightTransactionSimulation || $[6] !== flow.chainId) {
    t2 = evmPreflightTransactionSimulation && !(flow.chainId !== undefined && isSimulationUnsupportedSender(flow.chainId, account));
    $[4] = account;
    $[5] = evmPreflightTransactionSimulation;
    $[6] = flow.chainId;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const shouldSimulate = t2;
  let t3;
  if ($[8] !== latest) {
    t3 = () => {
      latestRef.current = latest;
    };
    $[8] = latest;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  useLayoutEffect(t3);
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => () => {
      attemptRef.current = attemptRef.current + 1;
    };
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== isDialogOpen) {
    t5 = [isDialogOpen];
    $[11] = isDialogOpen;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  useLayoutEffect(t4, t5);
  const idleState = flow.state.flowState === "idle" ? flow.state : undefined;
  const isIdle = idleState !== undefined;
  const finalError = getFinalGateError(idleState?.errorMessage, finalSimulationError);
  const currentError = screeningError ?? finalError;
  const canBypass = allowSimulationBypass && (currentError?.isBypassable ?? false);
  const skipSimulation = canBypass && isBypassAccepted;
  const isPending = pending !== null;
  useHoldActionFlow(isPending || currentError !== null);
  const ctaLabel = getCtaLabel({
    isPending,
    skipSimulation,
    currentError,
    flowLabel: flow.label
  });
  let t6;
  if ($[13] !== currentError || $[14] !== flow.state || $[15] !== isPending) {
    t6 = isPending || currentError ? {
      ...flow.state,
      errorMessage: undefined
    } : flow.state;
    $[13] = currentError;
    $[14] = flow.state;
    $[15] = isPending;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const actionFlowState = t6;
  let t7;
  if ($[17] !== account || $[18] !== onSimulationBypassChange || $[19] !== queryClient || $[20] !== setScreeningError || $[21] !== shouldSimulate || $[22] !== skipSimulation || $[23] !== trackScreeningCancel) {
    t7 = async () => {
      setScreeningError(retainBlockedScreeningError);
      setIsBypassAccepted(false);
      onSimulationBypassChange?.({
        skipFinalSimulation: false,
        userBypassed: false
      });
      attemptRef.current = attemptRef.current + 1;
      const attempt = attemptRef.current;
      const latestAtClick = latestRef.current;
      const address = account.address;
      const isCancelled = () => attemptRef.current !== attempt || !latestRef.current.isDialogOpen;
      const clearPendingAttempt = () => setPending(current => current === attempt ? null : current);
      const screeningResult = getScreeningFailure(address, isE2E, queryClient);
      setPending(attempt);
      const screeningFailure = await screeningResult;
      if (isCancelled()) {
        clearPendingAttempt();
        return;
      }
      if (screeningFailure) {
        clearPendingAttempt();
        reportScreeningFailure({
          failure: screeningFailure,
          chainId: latestRef.current.chainId,
          setScreeningError,
          trackScreeningCancel
        });
        return;
      }
      clearPendingAttempt();
      onSimulationBypassChange?.({
        skipFinalSimulation: skipSimulation || !shouldSimulate,
        userBypassed: skipSimulation
      });
      await latestAtClick.execute();
    };
    $[17] = account;
    $[18] = onSimulationBypassChange;
    $[19] = queryClient;
    $[20] = setScreeningError;
    $[21] = shouldSimulate;
    $[22] = skipSimulation;
    $[23] = trackScreeningCancel;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  const executeWithScreening = t7;
  const t8 = flow.isLoading || isPending;
  let t9;
  if ($[25] !== actionFlowState || $[26] !== ctaLabel || $[27] !== executeWithScreening || $[28] !== flow || $[29] !== t8) {
    t9 = {
      ...flow,
      execute: executeWithScreening,
      isLoading: t8,
      label: ctaLabel,
      state: actionFlowState
    };
    $[25] = actionFlowState;
    $[26] = ctaLabel;
    $[27] = executeWithScreening;
    $[28] = flow;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== canBypass || $[32] !== currentError || $[33] !== isBypassAccepted || $[34] !== isIdle) {
    t10 = isIdle && currentError ? <div data-testid="simulation-gate-error" className="gap-xs p-xs text-2xs bg-destructive-foreground/18 text-destructive-foreground rounded-lg"><div className="gap-xs flex flex-col"><div role="alert" className="gap-xs flex items-center"><Alert className="shrink-0" /><span>{currentError.message}</span></div>{canBypass ? <AcceptToContinueWithoutSimulationConfirmBox isAccepted={isBypassAccepted} setIsAccepted={setIsBypassAccepted} className="bg-transparent p-0 text-left" /> : null}</div></div> : null;
    $[31] = canBypass;
    $[32] = currentError;
    $[33] = isBypassAccepted;
    $[34] = isIdle;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  const t11 = isIdle && currentError?.isRetryBlocked ? null : children;
  let t12;
  if ($[36] !== t10 || $[37] !== t11 || $[38] !== t9) {
    t12 = <ActionFlowContext.Provider value={t9}>{t10}{t11}</ActionFlowContext.Provider>;
    $[36] = t10;
    $[37] = t11;
    $[38] = t9;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  return t12;
}